import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, BottomSheetContentLayout, Menu, Popover, Typography, useDevice, useOverlayContext } from 'seagull';
import { ExclamationMark, PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useLocaleKeys } from '~/i18n';
import { TriageCollapseWrapper } from '../TriageCollapseWrapper/TriageCollapseWrapper';
import { TriageFieldBox } from '../TriageFieldBox/TriageFieldBox';
import { TriageRow } from '../TriageRow/TriageRow';
import { TriageContactForm } from './TriageContactForm';
import s from './TriageContactRow.module.scss';
const formatContactSummary = (param)=>{
    let { companyName, firstName, lastName, email } = param;
    const name = [
        firstName,
        lastName
    ].filter((part)=>part != null && part !== '').join(' ');
    const hasCompany = companyName != null && companyName !== '';
    const hasName = name !== '';
    const hasEmail = email != null && email !== '';
    if (hasCompany && hasName && hasEmail) return "".concat(companyName, ", ").concat(name, " (").concat(email, ")");
    if (hasCompany && hasName) return "".concat(companyName, ", ").concat(name);
    if (hasCompany) return companyName;
    if (hasName && hasEmail) return "".concat(name, " (").concat(email, ")");
    if (hasName) return name;
    if (hasEmail) return email;
    return '';
};
export const GetTriageContactRowTID = (parentTestId)=>getTestIds(parentTestId);
export const PrimaryContactRowTestIds = GetTriageContactRowTID().PrimaryContactRowTestIds;
const MissingFieldsWarning = (param)=>{
    let { error } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    const skin = error ? 'error' : 'secondary';
    return /*#__PURE__*/ _jsxs("div", {
        className: s.missingFieldsWarning,
        "data-testid": PrimaryContactRowTestIds.MissingFieldsWarning(),
        children: [
            /*#__PURE__*/ _jsx(Typography, {
                skin: skin,
                children: /*#__PURE__*/ _jsx(ExclamationMark, {
                    size: 16
                })
            }),
            /*#__PURE__*/ _jsx(Typography, {
                skin: error ? 'error' : 'standard',
                children: t.primaryContactMissingFieldsWarning()
            })
        ]
    });
};
const ContactPickerMenu = (param)=>{
    let { contacts, selectedContactId, onSelectExisting, onSelectCreateNew } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    return /*#__PURE__*/ _jsx(Menu, {
        testId: PrimaryContactRowTestIds.ContactPicker.Root(),
        searchProps: {
            bi: {
                name: 'triage_contact_search'
            },
            showSearch: true,
            autoFocus: true,
            strategy: {
                type: 'custom',
                filterItem: (search, item)=>{
                    var _item_title, _item_body;
                    const haystack = "".concat((_item_title = item.title) !== null && _item_title !== void 0 ? _item_title : '', " ").concat((_item_body = item.body) !== null && _item_body !== void 0 ? _item_body : '').toLowerCase();
                    return haystack.includes(search.trim().toLowerCase());
                }
            }
        },
        items: contacts.map((c)=>({
                id: c.id(),
                testId: PrimaryContactRowTestIds.ContactPicker.Option(c.id()),
                title: c.companyName(),
                body: "".concat(c.fullName(), " (").concat(c.email(), ")"),
                selected: c.id() === selectedContactId,
                bi: {
                    name: 'triage_contact_picker_select_existing',
                    data: {
                        contact_id: c.id()
                    }
                },
                onClick: ()=>onSelectExisting(c)
            })),
        footer: {
            items: [
                {
                    variant: 'contextualAction',
                    props: {
                        bi: {
                            name: 'triage_contact_picker_create_new'
                        },
                        title: t.primaryContactContactsMenuCreateNew(),
                        icon: /*#__PURE__*/ _jsx(PlusCircle, {}),
                        testId: PrimaryContactRowTestIds.ContactPicker.CreateNew(),
                        onClick: onSelectCreateNew
                    }
                }
            ]
        }
    });
};
const ContactPickerSheet = (param)=>{
    let { contacts, selectedContactId, onSelectExisting, onSelectCreateNew } = param;
    const { t } = useLocaleKeys();
    const { hideBottomSheet } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        header: {
            title: t.agreementImport.triageModal.primaryContactMobileChangeButton(),
            gutter: 'm'
        },
        children: /*#__PURE__*/ _jsx(ContactPickerMenu, {
            contacts: contacts,
            selectedContactId: selectedContactId,
            onSelectExisting: (c)=>{
                onSelectExisting(c);
                hideBottomSheet();
            },
            onSelectCreateNew: onSelectCreateNew
        })
    });
};
const ContactFormSheet = (param)=>{
    let { client, formKey, onFieldChange, onValidityChange, onChangeRequest } = param;
    const { t } = useLocaleKeys();
    const { hideBottomSheet } = useOverlayContext();
    return /*#__PURE__*/ _jsx(BottomSheetContentLayout, {
        contentClassName: s.sheetContent,
        header: {
            title: t.agreementImport.triageModal.primaryContactFormTitle(),
            gutter: 'm'
        },
        footer: {
            primaryAction: {
                bi: {
                    name: 'save_triage_primary_contact'
                },
                text: t.common.buttons.save(),
                testId: PrimaryContactRowTestIds.MobileSaveButton(),
                onClick: ()=>hideBottomSheet()
            },
            secondaryAction: {
                text: t.agreementImport.triageModal.primaryContactMobileChangeButton(),
                testId: PrimaryContactRowTestIds.MobileChangeContactButton(),
                onClick: onChangeRequest
            }
        },
        children: /*#__PURE__*/ _jsx(TriageContactForm, {
            contact: client,
            onFieldChange: onFieldChange,
            onValidityChange: onValidityChange
        }, formKey)
    });
};
export const TriageContactRow = (param)=>{
    let { model, onModelChange, onFormValidityChange, requestValidation, formRef, inactivityCollapseMs = 2000 } = param;
    const { t } = useLocaleKeys('agreementImport.triageModal');
    const { isMobileDevice } = useDevice();
    const { pushBottomSheetPage, popBottomSheetPage } = useOverlayContext();
    const isUnmatched = model.isContactUnmatched();
    const hasAllRequired = model.contactHasAllRequiredFields();
    const [panelOpen, setPanelOpen] = useState(isUnmatched && !hasAllRequired);
    const [pickerAnchorEl, setPickerAnchorEl] = useState(null);
    const [resetFormCounter, setResetFormCounter] = useState(0);
    const resetFormCounterRef = useRef(0);
    const [interactionTick, setInteractionTick] = useState(0);
    const pickerOpen = pickerAnchorEl != null;
    const { contacts } = useContacts();
    const client = model.client();
    const selectedContactId = client.matchStatus === 'matched' ? client.matchedContactId : undefined;
    const modelRef = useRef(model);
    modelRef.current = model;
    useEffect(()=>{
        if (requestValidation && !hasAllRequired) {
            setPanelOpen(true);
        }
    }, [
        requestValidation,
        hasAllRequired
    ]);
    useEffect(()=>{
        if (!panelOpen || !hasAllRequired) return;
        const timer = setTimeout(()=>setPanelOpen(false), inactivityCollapseMs);
        return ()=>clearTimeout(timer);
    }, [
        panelOpen,
        hasAllRequired,
        interactionTick,
        inactivityCollapseMs
    ]);
    const handleFieldChange = useCallback((field, value)=>{
        onModelChange(modelRef.current.setContactField(field, value));
        setInteractionTick((tick)=>tick + 1);
    }, [
        onModelChange
    ]);
    const commitSelectExisting = (contact)=>{
        const phone = contact.phone();
        onModelChange(modelRef.current.matchContactToExisting(contact.id(), {
            companyName: contact.companyName(),
            firstName: contact.firstName(),
            lastName: contact.lastName(),
            email: contact.email(),
            phone: phone == null ? undefined : phone
        }));
    };
    const pushFormSheet = (sourceModel, onChangeRequest)=>{
        const c = sourceModel.client();
        const formKey = c.matchStatus === 'matched' ? c.matchedContactId : "unmatched-".concat(resetFormCounterRef.current);
        pushBottomSheetPage({
            content: /*#__PURE__*/ _jsx(ContactFormSheet, {
                client: c,
                formKey: formKey,
                onFieldChange: handleFieldChange,
                onValidityChange: onFormValidityChange,
                onChangeRequest: onChangeRequest
            })
        });
    };
    const pushPickerSheet = (onSelectCreateNew)=>{
        pushBottomSheetPage({
            fullHeight: true,
            content: /*#__PURE__*/ _jsx(ContactPickerSheet, {
                contacts: contacts,
                selectedContactId: selectedContactId,
                onSelectExisting: commitSelectExisting,
                onSelectCreateNew: onSelectCreateNew
            })
        });
    };
    const openFormSheetFlow = (sourceModel)=>{
        pushFormSheet(sourceModel, ()=>pushPickerSheet(()=>{
                const unmatched = modelRef.current.unmatchContact();
                onModelChange(unmatched);
                resetFormCounterRef.current += 1;
                setResetFormCounter(resetFormCounterRef.current);
                popBottomSheetPage();
                popBottomSheetPage();
                openFormSheetFlow(unmatched);
            }));
    };
    const openSheet = ()=>{
        if (isUnmatched) {
            openFormSheetFlow(modelRef.current);
        } else {
            pushPickerSheet(()=>{
                const unmatched = modelRef.current.unmatchContact();
                onModelChange(unmatched);
                popBottomSheetPage();
                openFormSheetFlow(unmatched);
            });
        }
    };
    const mobileBody = /*#__PURE__*/ _jsxs("div", {
        className: s.mobileSummaryBody,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: s.mobileSummaryLine,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: s.mobileSummaryText,
                        children: formatContactSummary(client)
                    }),
                    isUnmatched && /*#__PURE__*/ _jsx("span", {
                        "data-testid": PrimaryContactRowTestIds.NewContactBadge(),
                        className: s.mobileSummaryBadge,
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.primaryContactNewContactBadge()
                        })
                    })
                ]
            }),
            isUnmatched && !hasAllRequired && /*#__PURE__*/ _jsx(MissingFieldsWarning, {
                error: requestValidation
            })
        ]
    });
    const handleSelectExisting = (contact)=>{
        commitSelectExisting(contact);
        setPanelOpen(false);
        setPickerAnchorEl(null);
    };
    const handleCreateNew = ()=>{
        onModelChange(modelRef.current.unmatchContact());
        setPanelOpen(true);
        setPickerAnchorEl(null);
        resetFormCounterRef.current += 1;
        setResetFormCounter(resetFormCounterRef.current);
    };
    const actions = [
        ...isUnmatched ? [
            {
                text: t.primaryContactEditButton(),
                testId: PrimaryContactRowTestIds.EditButton(),
                onClick: ()=>setPanelOpen((o)=>!o)
            }
        ] : [],
        {
            text: t.primaryContactChangeButton(),
            testId: PrimaryContactRowTestIds.ChangeButton(),
            onClick: (event)=>setPickerAnchorEl(event.currentTarget)
        }
    ];
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": PrimaryContactRowTestIds.Root(),
        children: [
            /*#__PURE__*/ _jsx(TriageRow, {
                label: t.primaryContactLabel(),
                mobile: {
                    body: mobileBody,
                    bodyTestId: PrimaryContactRowTestIds.MobileSummaryBody(),
                    onEdit: openSheet,
                    editTestId: PrimaryContactRowTestIds.MobileEditButton(),
                    error: requestValidation && isUnmatched && !hasAllRequired
                },
                children: /*#__PURE__*/ _jsx(TriageFieldBox, {
                    variant: {
                        type: 'text',
                        value: formatContactSummary(client),
                        testId: PrimaryContactRowTestIds.Summary()
                    },
                    forceActionsVisible: pickerOpen,
                    badges: isUnmatched && /*#__PURE__*/ _jsx("span", {
                        "data-testid": PrimaryContactRowTestIds.NewContactBadge(),
                        children: /*#__PURE__*/ _jsx(Badge, {
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.primaryContactNewContactBadge()
                        })
                    }),
                    actions: actions
                })
            }),
            !isMobileDevice && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Popover, {
                        anchorEl: pickerAnchorEl,
                        open: pickerOpen,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                        },
                        onClose: ()=>setPickerAnchorEl(null),
                        children: /*#__PURE__*/ _jsx("div", {
                            "data-testid": PrimaryContactRowTestIds.ContactPicker.Root(),
                            children: /*#__PURE__*/ _jsx(ContactPickerMenu, {
                                contacts: contacts,
                                selectedContactId: selectedContactId,
                                onSelectExisting: handleSelectExisting,
                                onSelectCreateNew: handleCreateNew
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TriageCollapseWrapper, {
                        open: panelOpen,
                        testId: PrimaryContactRowTestIds.Panel(),
                        error: requestValidation && !hasAllRequired,
                        children: [
                            !hasAllRequired && /*#__PURE__*/ _jsx(MissingFieldsWarning, {}),
                            /*#__PURE__*/ _jsx(TriageContactForm, {
                                ref: formRef,
                                contact: client,
                                onFieldChange: handleFieldChange,
                                onValidityChange: onFormValidityChange,
                                triggerValidation: requestValidation
                            }, client.matchStatus === 'matched' ? client.matchedContactId : "unmatched-".concat(resetFormCounter))
                        ]
                    })
                ]
            })
        ]
    });
};
